// Reduces the portal's SSE run events into the shape the progress UI renders.
//
// The wire contract (services/data-pipeline/cmd/pipelinecontrol/observer.go):
//   step_start {name,total}
//   progress   {name,done,throughput}
//   step_end   {name,rows_in,rows_out,duration_ms,throughput}   ← success only
//   done       {status:'success'|'failed', duration_ms, error?, enrichment?}
//
// step_end is never emitted for a step that fails, so a failed run leaves its
// last step "running" on the wire. The reducer closes that step as failed, or
// the UI would spin on it forever.
//
// `done`'s optional `enrichment` field (enrichment runs only; absent on an import)
// carries enrichmentDoneView — per-step results including `err`. A step can fail
// while the RUN still reports status:'success' (cmd/pipelinecontrol never fails the
// header run over one step's error — see history_view.go's failedSteps doc — because
// that would make quality_store.go's LatestQuality discard every other step's real
// fills for the run). So failedSteps below is read off `enrichment.steps`, never off
// the top-level `status`.

export const initialRunState = {
  status: 'idle', // idle | running | success | failed
  steps: [],
  startedAt: null,
  durationMs: null,
  error: null,
  failedSteps: [] // [{name, err}] — steps that recorded an error even on a 'success' done
};

const patchStep = (steps, name, patch) =>
  steps.map((s) => (s.name === name ? { ...s, ...patch } : s));

export function runProgressReducer(state, event) {
  switch (event.type) {
    case 'start':
      return { ...initialRunState, status: 'running', startedAt: event.startedAt ?? Date.now() };

    case 'step_start':
      return {
        ...state,
        steps: [
          ...state.steps,
          {
            name: event.name,
            total: event.total || 0,
            done: 0,
            rowsIn: 0,
            rowsOut: 0,
            durationMs: null,
            throughput: 0,
            status: 'running'
          }
        ]
      };

    case 'progress':
      return {
        ...state,
        steps: patchStep(state.steps, event.name, {
          done: event.done || 0,
          throughput: event.throughput || 0
        })
      };

    case 'step_end':
      return {
        ...state,
        steps: state.steps.map((s) =>
          s.name === event.name
            ? {
                ...s,
                status: 'success',
                rowsIn: event.rows_in || 0,
                rowsOut: event.rows_out || 0,
                durationMs: event.duration_ms ?? null,
                throughput: event.throughput || 0,
                // A finished step reads 100% even if the last progress tick
                // landed mid-batch, so its meter always fills.
                done: s.total || event.rows_in || s.done
              }
            : s
        )
      };

    case 'done': {
      const failed = event.status === 'failed';
      const enrichSteps = (event.enrichment && event.enrichment.steps) || [];
      return {
        ...state,
        status: failed ? 'failed' : 'success',
        durationMs: event.duration_ms ?? state.durationMs,
        error: failed ? event.error || 'Import failed' : null,
        // Close out any step the pipeline abandoned mid-flight.
        steps: state.steps.map((s) =>
          s.status === 'running' ? { ...s, status: failed ? 'failed' : 'success' } : s
        ),
        failedSteps: enrichSteps.filter((s) => s.err).map((s) => ({ name: s.name, err: s.err }))
      };
    }

    default:
      return state;
  }
}

// Rows actually written by the steps that finished.
export function totalRowsLanded(state) {
  return state.steps.reduce((n, s) => n + (s.status === 'success' ? s.rowsOut || 0 : 0), 0);
}

export function currentStep(state) {
  return state.steps.find((s) => s.status === 'running') || null;
}
